import React, {useMemo, useState} from "react";
import Button from "../../../../../components/button/button.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import {FaCar, FaPen, FaShareSquare, FaTrash} from "react-icons/fa";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../../../utils/ag-grid-columns-utils.js";
import projectUtils from "../../../../../utils/project-utils.js";
import {activityLinkUrl, formatDateTime} from "../utils/activity-utils.js";
import ActivityLink from "./activity-link.jsx";

const GarrisonDetails = ({garrison}) => {
    const {team, intendedFunction, sectors, initialObservation, composition = [], animalModel = []} = garrison;
    const first = composition[0];
    const initialMileage = first?.initialMileage;
    const finalMileage = first?.finalMileage;
    const showMileage = initialMileage || finalMileage;
    const validMileage = initialMileage && finalMileage && finalMileage >= initialMileage;
    const animals = (animalModel ?? []).map(animal => animal.name).join(" | ");

    return (
        <div className="text-sm space-y-2">
            <div className="grid grid-cols-12 gap-2">
                <div className="col-span-3"><strong>Equipe: </strong>{team ?? ""}</div>
                <div className="col-span-3"><strong>Função destinada: </strong>{intendedFunction ?? ""}</div>
                <div className="col-span-6"><strong>Setores: </strong>{(sectors ?? []).join(" | ")}</div>
                <div className="col-span-12"><strong>Observação inicial: </strong>{initialObservation ?? ""}</div>
            </div>

            {showMileage &&
                <div className="grid grid-cols-3 gap-2">
                    <div><strong>Km inicial: </strong>{initialMileage ?? ""}</div>
                    <div><strong>Km final: </strong>{finalMileage ?? ""}</div>
                    <div>
                        <strong>Km percorrido: </strong>
                        {validMileage ? (finalMileage - initialMileage) : <span className="text-red-600">Inconsistente</span>}
                    </div>
                </div>
            }

            {composition.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-2">
                    <div className="col-span-12"><strong>Composição: </strong>{item.name}</div>
                    {(item.integrants ?? []).map((integrant, position) => {
                        const isInspector = integrant.contingent?.rank === "INSPETOR";
                        return (
                            <div key={position} className="col-span-6">
                                {integrant.contingent?.namewar} | {integrant.position}{isInspector ? ` (${integrant.contingent.rank})` : ""}
                            </div>
                        );
                    })}
                </div>
            ))}

            <div><strong>Animais: </strong>{animals}</div>
        </div>
    );
};

const DispatchList = ({list = []}) => {
    const columnDefs = useMemo(() => [
        {
            field: "numberCheckbook", headerName: "Talão", width: 130,
            cellRenderer: ({data, value}) => (
                <ActivityLink to={activityLinkUrl.dispatch(data?.id)}>{value ?? ""}</ActivityLink>
            ),
        },
        {field: "start", headerName: "Início", width: 150},
        {field: "nature", headerName: "Natureza", flex: 1},
        {field: "neighborhood", headerName: "Bairro", flex: 1},
        {field: "street", headerName: "Logradouro", flex: 1},
        {field: "number", headerName: "Nº", width: 90},
        {field: "finalobservation", headerName: "Obs. Final", flex: 1},
    ], []);

    return (
        <AgGridReact
            domLayout="autoHeight"
            rowData={list}
            columnDefs={columnDefs}
            defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
            animateRows={false}
            suppressCellFocus={true}
            gridOptions={{localeText: aggridUtils.localization()}}
            theme={aggridUtils.generalTheme()}
            overlayNoRowsTemplate="Nenhum despacho."
        />
    );
};

const ActivityGarrisonCard = ({garrison, onChangeObservation, onRemove, boName, isLast}) => {
    const [showDetails, setShowDetails] = useState(false);
    const [showDispatch, setShowDispatch] = useState(false);

    const remove = () => {
        projectUtils.swalMessage("warning", "Excluir",
            `Excluir a guarnição "${garrison.name}"? Os respectivos ${boName} e abordagens também serão excluídos.`,
            "Sim", true, "Não",
            ({isConfirmed}) => {
                if (isConfirmed) onRemove(garrison.id);
            });
    };

    return (
        <div className={isLast ? "" : "border-b border-[#D2D3D5]"}>
            <div className="grid grid-cols-12 gap-2 items-center px-2 py-1 text-[14px] hover:bg-[#eef3fb]">
                <span className="col-span-2">{formatDateTime(garrison.start)}</span>
                <span className="col-span-2">{formatDateTime(garrison.end)}</span>
                <span className="col-span-3">
                    <ActivityLink to={activityLinkUrl.garrison(garrison.id)}>{garrison.name}</ActivityLink>
                </span>
                <div className="col-span-5 flex items-center gap-2">
                    <InputForm
                        classNameWrapper="flex-1 min-w-0" id={`garrison-observation-${garrison.id}`} label=""
                        value={garrison.garrisonObservation ?? ""}
                        onChange={({target: {value}}) => onChangeObservation(garrison.id, value)}
                    />
                    <div className="flex gap-1 shrink-0">
                        <Button
                            className="px-2!"
                            target="primary-outline"
                            title="Detalhes da guarnição"
                            onClick={() => { setShowDispatch(false); setShowDetails(prev => !prev); }}
                        >
                            <FaCar/>
                        </Button>

                        <Button
                            className="px-2!"
                            target="primary-outline"
                            title="Lista de despachos"
                            onClick={() => { setShowDetails(false); setShowDispatch(prev => !prev); }}
                        >
                            <FaShareSquare/>
                        </Button>

                        <Button className="px-2!" target="danger" title="Remover guarnição" onClick={remove}><FaTrash/></Button>
                    </div>
                </div>
            </div>
            {showDetails && <div className="border-t border-[#D2D3D5] p-3 bg-white"><GarrisonDetails garrison={garrison}/></div>}
            {showDispatch && <div className="border-t border-[#D2D3D5] p-3 bg-white"><DispatchList list={garrison.listDispatch}/></div>}
        </div>
    );
};

const ActivityGarrisonList = ({garrisons, onChangeObservation, onRemove, boName}) => {
    return (
        <div className="overflow-hidden rounded-lg border border-[#D2D3D5] text-[#676a6c]">
            <div className="grid grid-cols-12 gap-2 items-center bg-[#efefef] px-2 h-10 text-[14px] font-bold border-b border-[#D2D3D5]">
                <span className="col-span-2">Início</span>
                <span className="col-span-2">Fim</span>
                <span className="col-span-3">Guarnição</span>
                <span className="col-span-5 flex items-center gap-1.5">
                    <FaPen size={11} className="text-gray-500"/>
                    Observação
                </span>
            </div>

            {!garrisons.length &&
                <div className="px-2 h-7.5 text-center text-sm content-center">Nenhum registro.</div>
            }

            {garrisons.map((garrison, index) => (
                <ActivityGarrisonCard
                    key={garrison.id}
                    garrison={garrison}
                    boName={boName}
                    isLast={index === garrisons.length - 1}
                    onChangeObservation={onChangeObservation}
                    onRemove={onRemove}
                />
            ))}
        </div>
    );
};

export default ActivityGarrisonList;
